import * as React from 'react';

/**
 * Stops a Radix overlay's close-time trigger refocus from painting a stuck
 * focus ring after a pointer dismiss.
 *
 * Radix restores focus to the trigger on close (`onCloseAutoFocus` then
 * `triggerRef.focus()`), and browsers treat that script-driven focus as
 * `:focus-visible`, so a mouse user who opens a menu and clicks out sees the
 * trigger's focus ring stay on until they click somewhere else. The guard
 * cancels the refocus for pointer closes only: a click outside the content,
 * or a second click on the trigger that toggles the overlay shut. A keyboard
 * close (Escape) keeps the refocus, so keyboard users are not dropped on
 * `<body>` and their focus stays visible (WCAG 2.4.7).
 *
 * Item selection inside the content is deliberately untouched: the refocused
 * trigger is what a dialog opened by the item restores focus to on its own
 * close, and breaking that chain strands keyboard users two layers deep.
 *
 * Sibling of {@link useOverlayTriggerTooltip}, which rides the same
 * close-auto-focus seam to keep the trigger's tooltip from flashing open.
 */
export const useOverlayTriggerRefocusGuard = (isOpen: boolean) => {
  const suppressRefocusRef = React.useRef(false);

  const onTriggerPointerDown = React.useCallback(() => {
    // A pointer-down on the trigger while the overlay is open is a toggle
    // close; one while it is closed is the open, which must not arm the guard
    // or a keyboard close right after a pointer open would lose its refocus.
    if (isOpen) suppressRefocusRef.current = true;
  }, [isOpen]);

  const onPointerDownOutside = React.useCallback(() => {
    suppressRefocusRef.current = true;
  }, []);

  const onEscapeKeyDown = React.useCallback(() => {
    suppressRefocusRef.current = false;
  }, []);

  const onCloseAutoFocus = React.useCallback((event: Event) => {
    if (suppressRefocusRef.current) {
      suppressRefocusRef.current = false;
      event.preventDefault();
    }
  }, []);

  // Arms the guard for a close the overlay itself decides to trigger
  // programmatically (e.g. a hover-driven close that bypasses the trigger's
  // own pointerdown/outside-click handlers) — call right before closing.
  const suppressNextRefocus = React.useCallback(() => {
    suppressRefocusRef.current = true;
  }, []);

  return {
    /** Spread onto the overlay's trigger element. */
    triggerProps: { onPointerDown: onTriggerPointerDown },
    /** Spread onto the overlay's content component. */
    contentProps: { onPointerDownOutside, onEscapeKeyDown, onCloseAutoFocus },
    /** Call before a programmatic close that isn't a trigger pointerdown or
     * an outside click, so the trigger doesn't reclaim focus on this close
     * either. */
    suppressNextRefocus,
  };
};
